'use client';

import type { Hook } from './types';

import { useEffect, useState } from 'react';

/**
 * A custom React hook to check if the viewport width matches a media query.
 * It is SSR-safe and only runs on the client.
 *
 * @param {number} breakpoint - The width in pixels to check against (e.g., 768).
 * @returns {boolean} - Returns `true` if the viewport width is greater than or equal to the breakpoint, otherwise `false`.
 */
export const useMediaQuery: Hook = ({ breakpoint }) => {
  // State to store whether the media query matches.
  // We initialize to `false` on the server and calculate the real value on the client.
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    // This effect only runs on the client side, where the `window` object is available.

    // 1. Function to check the media query and update the state.
    const checkQuery = () => {
      setMatches(window.innerWidth >= breakpoint);
    };

    // 2. Run the check immediately on component mount (client-side).
    checkQuery();

    // 3. Add an event listener to re-evaluate on window resize.
    window.addEventListener('resize', checkQuery);

    // 4. Cleanup function: Remove the event listener when the component unmounts
    //    to prevent memory leaks.
    return () => {
      window.removeEventListener('resize', checkQuery);
    };
  }, [breakpoint]); // Re-run the effect if the breakpoint prop changes.

  return matches;
};
